import { useMemo, useState } from 'react';
import { Server as ServerIcon } from 'lucide-react';
import {
  Button,
  Input,
  Switch,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { Field } from '@vnetwork/ui/forms';
import { useClusterNodes, nodeIp, nodeRole, nodeName, nodeReady } from '@/api/k8s';
import { useUpdateLoadBalancerForwards, type LbForward } from '@/api/opennebula';

// L4 forwards run on the LVS service (keepalived + IPVS) — TCP and UDP only. This path
// is separate from HAProxy (the ingress controller's listeners); a forward never touches
// it. Common case: UDP DNS on :53.
const PROTOCOLS = ['TCP', 'UDP'];

/**
 * Quick forward-port dialog. Exposes a public port on the cluster's ingress load
 * balancer that forwards to a `nodePort` on the selected worker nodes — the same
 * node IP + nodePort pair a `type: NodePort` Service is reachable on. Backends are
 * auto-filled from the cluster's worker node IPs so the operator only picks a port.
 */
export function QuickForwardDialog({
  serviceId,
  clusterId,
  computeSvc,
  lbId,
  existing,
  onClose,
}: {
  serviceId: string;
  clusterId: string;
  computeSvc: string;
  lbId: string;
  existing: LbForward[];
  onClose: () => void;
}) {
  const { show } = useToast();
  const { data, isLoading } = useClusterNodes(serviceId, clusterId);

  // Worker nodes with a routable internal IP — these become the forward backends.
  const workers = useMemo(
    () => (data?.nodes ?? []).filter((n) => nodeRole(n) === 'worker' && !!nodeIp(n)),
    [data],
  );

  const [protocol, setProtocol] = useState('TCP');
  const [port, setPort] = useState('');
  const [nodePort, setNodePort] = useState('');
  // Node IPs to include; seeded to all workers once they load.
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const selected = workers.filter((n) => !excluded.has(nodeIp(n)));

  const toggle = (ip: string) =>
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(ip)) next.delete(ip);
      else next.add(ip);
      return next;
    });

  const save = useUpdateLoadBalancerForwards(computeSvc, lbId, {
    onSuccess: () => { show({ tone: 'success', title: 'Forward created' }); onClose(); },
    onError: () => show({ tone: 'danger', title: 'Create failed' }),
  });

  const valid = !!port && !!nodePort && selected.length > 0;

  const submit = () => {
    const forward: LbForward = {
      protocol,
      port,
      servers: selected.map((n) => ({ host: nodeIp(n), port: nodePort })),
    };
    save.mutate([...existing, forward]);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="lg">
        <DialogHeader><DialogTitle>Quick forward port</DialogTitle></DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--vn-space-3)' }}>
              <Field label="Protocol">
                {() => (
                  <Select value={protocol} onValueChange={setProtocol}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{PROTOCOLS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Public port" description="On the LB IP">
                {(p) => <Input {...p} type="number" placeholder="e.g. 8080" value={port} onChange={(e) => setPort(e.target.value)} />}
              </Field>
              <Field label="Node port" description="Target on workers">
                {(p) => <Input {...p} type="number" placeholder="e.g. 30080" value={nodePort} onChange={(e) => setNodePort(e.target.value)} />}
              </Field>
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--vn-space-2)' }}>
                <span style={{ fontWeight: 600, fontSize: 'var(--vn-font-size-sm)' }}>Backend nodes</span>
                <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>{selected.length}/{workers.length} selected</span>
              </div>
              {isLoading ? (
                <p style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>Loading worker nodes…</p>
              ) : workers.length === 0 ? (
                <Alert tone="warning" title="No worker nodes">This cluster has no worker nodes with a routable IP yet.</Alert>
              ) : (
                <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
                  {workers.map((n) => {
                    const ip = nodeIp(n);
                    const ready = nodeReady(n);
                    return (
                      <label
                        key={ip}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--vn-space-2)',
                          padding: '6px var(--vn-space-3)', border: '1px solid var(--vn-border-subtle)', borderRadius: 'var(--vn-radius-md)',
                          fontSize: 'var(--vn-font-size-sm)',
                        }}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)', minWidth: 0 }}>
                          <ServerIcon size={14} style={{ color: 'var(--vn-fg-subtle)', flexShrink: 0 }} />
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{nodeName(n)}</span>
                          <code style={{ fontFamily: 'var(--vn-font-mono)', color: 'var(--vn-fg-muted)' }}>{ip}</code>
                          {!ready && <span style={{ color: 'var(--vn-fg-subtle)' }}>· not ready</span>}
                        </span>
                        <Switch checked={!excluded.has(ip)} onCheckedChange={() => toggle(ip)} />
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <Alert tone="info" title="Separate from ingress">
              This forward runs on the load balancer's L4 path (LVS/IPVS), independent of the ingress controller's
              listeners — it won't affect ingress traffic. Backends are the selected worker nodes at the nodePort.
            </Alert>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={!valid || save.isPending} onClick={submit}>Create forward</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
